import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GenericMetricFormatterPipe } from 'src/app/reusables/pipes/generic-metric-formatter.pipe';
import { GamesService } from '../../../services/games.service';
import { MarketplaceService } from '../../../services/marketplace.service';
import { IHGameStandardize, IHSelectedMetric, StandardizeRewards } from '../../../models/games-parameter.model';
import { isConnect3 } from '../../../models/games-player-breakdown';

type CustomReward = {
  PointStoreProductID: number;
  Title: string;
};

type RaffleRewardsProducts = {
  [key: number]: CustomReward;
};

@Component({
  selector: 'cv-games-details-modal',
  templateUrl: './games-details-modal.component.html',
  styleUrls: ['./games-details-modal.component.scss']
})
export class GamesDetailsModalComponent implements OnInit {
  @Input() hGame: IHGameStandardize;

  gs = null;
  game = null; 
  gameData = null;
  metricCompleted = [];
  milestoneThemes = [];
  themeSelected: any;
  raffleRewardsProducts: RaffleRewardsProducts = {};

  constructor(
    private gamesService: GamesService,
    private metricFormatPipe: GenericMetricFormatterPipe,
    private activeModal: NgbActiveModal,
    private marketplaceService: MarketplaceService // we already use this service in a parent view, so there is no need to load the values again, but we can use it in order to know the products that we won in the raffle game
  ) { 
    this.gs = this.gamesService.data;
  }

  ngOnInit(): void {
    this.game = this.gs.selectedGame[0];
    this.gameData = this.gs.selectedGame[0];
    this.metricCompleted = [];
    this.raffleRewardsProducts = this.marketplaceService.customRewards.reduce((acc, element)=>{
      return {
        ...acc,
        [element.PointStoreProductID]: element,
      }
    },{});
  }

  close = function () {
    this.activeModal.close();
  }

  get gameName() {
    if (this.hGame) return this.hGame.RuleName;
    return this.gs.selectedGame[0].underlyingRule.RuleName;
  }

  // return true if the game is connect 3 or connect all
  get isConnectGame(){
    return this.gameData.underlyingRule.GameType == 6 || this.gameData.underlyingRule.GameType == 7;
  }

  get singleMetricName() {
    if (this.hGame && this.hGame.metricDefinition) {
      const metric = this.hGame.metricDefinition;
      if (metric && metric.MetricDetail?.Abbrev) return metric.MetricDetail?.Abbrev;
    }
    return this.game.SelectedMetrics[0]?.abbrev;
  }

  get qualifierName() {
    if (!this.hGame) return this.getLocalizedText("None", "None");
    const qualifierDetail = this.hGame?.qualifierDefinition?.MetricDetail;
    const qualifierName = qualifierDetail?.Abbrev ? qualifierDetail?.Abbrev : this.getLocalizedText("None", "None");
    return qualifierName;
  }

  get isValidQualifier() {
    if (!this.hGame) return false;
    return !!this.hGame.qualifierDefinition;
  }

  get qualifierMetric() {
    if (!this.hGame) return null;
    return this.hGame.qualifierDefinition;
  }

  get connect3Metrics(){
    if (!this.hGame || !this.hGame.Connect3Definition) return [];
    return this.hGame.Connect3Definition;
  }

  get rewards(): StandardizeRewards[] {
    if (!this.hGame) return [];
    return this.hGame.rewards;
  }
  
  getBadge (game, index) {
    var reward = null;
    if (game.TopDogRewards != null) {
      if (game.TopDogRewards.length > index){
        if (game.TopDogRewards[index].Badge != undefined || game.TopDogRewards[index].Badge != null) {
          reward = game.TopDogRewards[index].Badge.rewardValue
        } else {
          reward = null;
        }
      }
    } // get 1st place reward
    else if (game.BullseyeRewards != null) { 
      reward = (game.BullseyeRewards.length != 0 && game.BullseyeRewards[index].Badge != null) ? game.BullseyeRewards[index].Badge.rewardValue : null; 
    }
    else if (game.MilestoneRewards != null) {
      reward = (game.MilestoneRewards.length != 0 && game.MilestoneRewards[index].Badge != null) ? game.MilestoneRewards[index].Badge.rewardValue : null; 
    }

    if (reward == null || reward == 0) return null;
    
    return "../Admin/ImageResizer.aspx?imgid=" + reward + "&w=24&h=24";
  }
  
  getConnect3Class (metricPosition: IHSelectedMetric) {
    const classes: any = {};
    if (!this.hGame) return classes;
    if (metricPosition == null || metricPosition.MetricID === null) {
      classes.simpleCard = true;
      return classes;
    }

    if (metricPosition.MetricID == -1) {
      if (isConnect3(this.hGame.GameType)) classes.freeSpace = true; 
      else classes.freeSpaceBlackout = true;
      return classes;
    }
    if (isConnect3(this.hGame.GameType)) classes.cardConfiguredConnect3 = true;
    else classes.cardConfiguredBlackout = true;
    return classes;
  }

  getCheck (position) {
    return (this.metricCompleted[position]) ? true : false;
  }
  
  getCheckmark (index: number) {
    if (this.gameData == null) return;

    this.milestoneThemes = this.gamesService.getGameDefinitions().filter((gt)  => { return gt.GameType == 5 })[0].Themes;
    this.themeSelected = this.milestoneThemes[this.gameData.MilestoneDetails.MilestoneThemeSelected.themeID - 1];
    return { 'background-image': 'url(' + this.themeSelected.themePath + "checkmark" + (index + 1) + ".svg" + ')' }
  }

  getLocalizedText(key: string, defaultText: string): string{
    return this.gamesService.getLocalizedMessage(key, defaultText);
  }

  getFormattedMetric(value, format: string = ''): string{
    return this.metricFormatPipe.transform(value, format);
  }

  getBadgeImage (game: any, i: number) {
    var reward = null;
    if (game == undefined) return reward;
    if (game.MultiplierMadnessRewards != null) { reward = null; } // No badges for MM
    else if (game.TopDogRewards != null) { reward = (game.TopDogRewards.length != 0 && game.TopDogRewards[0].Badge != null) ? game.TopDogRewards[0].Badge.rewardValue : null; } // get 1st place reward
    else if (game.BullseyeRewards != null) { reward = (game.BullseyeRewards.length != 0 && game.BullseyeRewards[0].Badge != null) ? game.BullseyeRewards[0].Badge.rewardValue : null; }
    else if (game.Connect3Rewards != null && game.underlyingRule.GameType == 6) { reward = (game.Connect3Rewards.ThreeInRow.length != 0 && game.Connect3Rewards.ThreeInRow != null) ? game.Connect3Rewards.ThreeInRow.badge : null; }
    else if (game.Connect3Rewards != null && game.underlyingRule.GameType == 7) { reward = (game.Connect3Rewards.Blackout.length != 0 && game.Connect3Rewards.Blackout != null) ? game.Connect3Rewards.Blackout.badge : null; }
    else if (game.MilestoneRewards != null && game.MilestoneRewards.length > 0) { reward = (game.MilestoneRewards[0].Badge != null) ? game.MilestoneRewards[game.MilestoneRewards.length - 1].Badge.rewardValue : null; }
    else if (game.BracketsTournamentRewards != null && game.BracketsTournamentRewards.length > 0) { reward = (game.BracketsTournamentRewards[i]?.Badge != null) ? game.BracketsTournamentRewards[i].Badge.rewardValue : null; }
    if (reward == null || reward == 0) return null;
    return "../Admin/ImageResizer.aspx?imgid=" + reward + "&w=24&h=24";
  }
  
  getRank(i: number): string {
    const index = i + 1;
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const indexMod100 = index % 100;
    const suffix = suffixes[(index - 20) % 100] || suffixes[indexMod100] || suffixes[0];
    return `${index}${suffix}`;
  }

  get metricValue() : IHSelectedMetric | null {
    if (!this.hGame) return null;    
    return this.hGame.metricDefinition;
  }

  getBracketTournamentPlayers(rounds: number) : number {
    return Math.pow(2, rounds);
  }

  getBracketTournamentMatches(rounds: number) : number {
    return Math.pow(2, rounds) - 1;
  }
}
